import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EmbedDimensions from './embed-dimensions';

type Props = {
  open: boolean;
  width: number;
  height: number;
  onClose: () => void;
  html?: string;
  onApply?: (payload: { ratio: number; zoom: number }) => void;
  initialRatio?: number;
};

const EmbedDimensionsPopup: React.FC<Props> = ({ open, width, height, html = '', onClose, onApply, initialRatio }) => {
  // Aspect ratio presets (width / height)
  const RATIOS: { label: string; value: number }[] = [
    { label: '16:9 (HD)', value: 16/9 },
    { label: '19:9 (Ultra‑wide)', value: 19/9 },
    { label: '21:9 (Cinema)', value: 21/9 },
    { label: '32:9 (Super‑ultra‑wide)', value: 32/9 },
    { label: '2:1', value: 2/1 },
    { label: '18:9', value: 18/9 },
    { label: '3:2', value: 3/2 },
    { label: '4:3', value: 4/3 },
    { label: '5:4', value: 5/4 },
    { label: '1:1 (Square)', value: 1 },
    { label: '9:16 (Portrait)', value: 9/16 },
    { label: '4:5 (Portrait)', value: 4/5 },
  ];

  const snapRatio = (r: number | undefined | null) => {
    const rr = Math.max(0.0001, Number(r) || 16/9);
    let best = RATIOS[0]?.value ?? 16/9;
    let bestErr = Math.abs((RATIOS[0]?.value ?? 16/9) - rr);
    for (let i = 1; i < RATIOS.length; i++) {
      const val = RATIOS[i].value;
      const err = Math.abs(val - rr);
      if (err < bestErr) { best = val; bestErr = err; }
    }
    return best;
  };

  const [ratio, setRatio] = useState<number>(() => snapRatio(initialRatio));
  const fitToRatio = (w: number, h: number, r: number) => {
    const targetH = Math.max(1, Math.round(w / Math.max(0.0001, r)));
    const z = h / targetH; // scale to fit available height
    return Math.max(0.25, Math.min(1, z));
  };
  const [zoom, setZoom] = useState<number>(() => fitToRatio(width, height, snapRatio(initialRatio)));
  const dialogRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    setZoom(fitToRatio(width, height, ratio));
  }, [width, height, ratio]);
  useEffect(() => { /* no doc-level capture guard; rely on bubble stopPropagation below */ }, [open]);
  useEffect(() => {
    if (open) {
      const snapped = snapRatio(initialRatio);
      setRatio(snapped);
      setZoom(fitToRatio(width, height, snapped));
    }
  }, [open, initialRatio, width, height]);
  if (!open) return null;
  const portalRoot = typeof document !== 'undefined' ? document.body : null;
  const modal = (
    <div
      className="fixed inset-0 z-[2147483647]"
    >
      <div
        className="absolute inset-0 bg-black/40"
        data-backdrop="true"
        onClick={onClose}
      />
      <div
        className="fixed inset-0 overflow-auto p-5 flex items-start justify-center"
        aria-modal="true"
        role="dialog"
        data-editor-action
        ref={dialogRef}
        onMouseDown={(e)=>{ e.stopPropagation(); }}
        onClick={(e)=>{ e.stopPropagation(); }}
      >
        <div
          className="relative w-full max-w-5xl max-h-[90vh] overflow-auto rounded-2xl border border-gray-200 bg-white shadow-2xl my-6"
          data-editor-action
          onMouseDown={(e)=>{ e.stopPropagation(); }}
          onClick={(e)=>{ e.stopPropagation(); }}
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-gray-900">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 21V3h18"/><path d="M18 17v-2"/><path d="M14 17v-6"/><path d="M10 17v-4"/><path d="M6 17v-8"/></svg>
              </span>
              Embed Dimensions
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <span>Aspect</span>
                <select
                  className="h-7 rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-800"
                  value={String(ratio)}
                  onChange={(e)=> setRatio(Number(e.target.value))}
                >
                  {RATIOS.map(r => (
                    <option key={r.label} value={String(r.value)}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <span>Zoom</span>
                <input
                  type="range"
                  min={0.25}
                  max={3}
                  step={0.05}
                  value={zoom}
                  onChange={(e)=> setZoom(parseFloat(e.target.value))}
                  className="w-40"
                />
                <span className="tabular-nums w-10 text-right">{Math.round(zoom*100)}%</span>
              </div>
              <button
                className="rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-100"
                onClick={() => { try { onApply && onApply({ ratio: snapRatio(ratio), zoom }); } finally { try { onClose && onClose(); } catch {} } }}
              >Apply</button>
              <button className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50" onClick={onClose}>Close</button>
            </div>
          </div>
          <div className="p-4">
            {(() => {
              const scale = Math.max(0.1, Math.min(3, zoom));
              const invPercent = (100 / scale) + '%';
              const vpH = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 800;
              const chrome = 260; // header + paddings + controls
              const maxCanvasH = Math.max(180, vpH - chrome);
              const targetH = Math.min(Math.max(1, Math.round(width / Math.max(0.0001, ratio))), maxCanvasH);
              const srcDoc = `<!doctype html><html><head><meta charset=\"utf-8\"/><meta name=\"viewport\" content=\"width=device-width, initial-scale=1\"/><style>html,body{margin:0;padding:0;overflow:hidden}</style></head><body>${html||''}</body></html>`;
              return (
                <div className="rounded-lg border border-gray-200 bg-white p-3">
                  <div className="relative overflow-hidden rounded-md border border-gray-200 bg-gray-50" style={{ width: '100%', height: targetH }}>
                    <div style={{ width: invPercent, height: invPercent, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
                      <iframe
                        title="Embed Preview"
                        srcDoc={srcDoc}
                        className="h-full w-full"
                        sandbox="allow-scripts allow-same-origin allow-forms"
                      />
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
  return portalRoot ? createPortal(modal, portalRoot) : modal;
};

export default EmbedDimensionsPopup;
